import { useState } from 'react';
import { User } from '../models/User';
import { sendMessage } from '../data/messageDatabase';

export interface UseContactUserReturn {
  isOpen: boolean;
  recipientId: string | null;
  source: 'Timeline' | 'Meetings' | 'Marketplace' | 'Financial Reports' | null;
  showSuccessPopup: boolean;
  successRecipientId: string | null;
  openContactModal: (recipientId: string, source: 'Timeline' | 'Meetings' | 'Marketplace' | 'Financial Reports') => void;
  closeContactModal: () => void;
  closeSuccessPopup: () => void;
  triggerSuccessPopup: (recipientId: string) => void;
  sendMessage: (recipientId: string, content: string, source: 'Timeline' | 'Meetings' | 'Marketplace' | 'Financial Reports') => Promise<void>;
}

export const useContactUser = (currentUser: User): UseContactUserReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const [source, setSource] = useState<'Timeline' | 'Meetings' | 'Marketplace' | 'Financial Reports' | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successRecipientId, setSuccessRecipientId] = useState<string | null>(null);

  const openContactModal = (recipientId: string, source: 'Timeline' | 'Meetings' | 'Marketplace' | 'Financial Reports') => {
    setRecipientId(recipientId);
    setSource(source);
    setIsOpen(true);
  };

  const closeContactModal = () => {
    setIsOpen(false);
    setRecipientId(null);
    setSource(null);
  };

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
    setSuccessRecipientId(null);
  };

  const triggerSuccessPopup = (recipientId: string) => {
    setSuccessRecipientId(recipientId);
    setShowSuccessPopup(true);
  };

  const sendMessageWithSource = async (recipientId: string, content: string, source: 'Timeline' | 'Meetings' | 'Marketplace' | 'Financial Reports'): Promise<void> => {
    // Validate inputs
    if (!currentUser || !currentUser.id) {
      throw new Error('User not authenticated');
    }
    
    if (!recipientId) {
      throw new Error('Recipient not specified');
    }
    
    if (!content.trim()) {
      throw new Error('Message content is empty');
    }
    
    // Add source information to the message content with double line breaks
    const messageWithSource = `**Question from ${source}**\n\n\n${content}`;
    
    try {
      // Use the optimized messaging system with sender data
      await sendMessage(currentUser.id, recipientId, messageWithSource, undefined, currentUser);
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  };

  return {
    isOpen,
    recipientId,
    source,
    showSuccessPopup,
    successRecipientId,
    openContactModal,
    closeContactModal,
    closeSuccessPopup,
    triggerSuccessPopup,
    sendMessage: sendMessageWithSource
  };
}; 